import React, { useEffect, useMemo, useState } from 'react'
import clsx from 'clsx'
import Decimal from 'decimal.js'
import { makeStyles } from '@mui/styles'
import { Tooltip } from '@mui/material'

import lang from '@/lang'
import ThemeInterface from '@/theme-interface'
import { brandOutlineButton } from '../../_helpers/brandButtons'
import { brandHeading } from '@/utils/brand-typography'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import { ButtonSquare } from '@/modules/base/components/buttons'
import { VARIANTS as BUTTON_VARIANTS } from '@/modules/base/components/buttons/square/button-square'
import QuantityInput from '@/modules/base/components/inputs/quantity-input/quantity-input'
import { ModuleTypes, dateConstants } from '@/modules/base/util/constants'
import { Module } from '@/modules/moduleSubscription/types/module'
import formatPrice from '../../_helpers/formatPrice'
import { getAddonIcon, getAddonTitle } from '../../_helpers/addonDisplay'
import { ModuleIconMap } from '@/modules/moduleSubscription/components/list/module-item/module-item'
import { getMaxCartCount, STORAGE_PACKAGE_SIZE_GB } from '@/modules/moduleSubscription/constants'

const useStyles = makeStyles((theme: ThemeInterface) => ({
  // the whole tile toggles the add-on; a selected one has a 2 px border (the padding keeps the content in place)
  root: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    gap: 24,
    padding: 24,
    border: `1px solid ${theme.palette.brand.inputBorder}`,
    borderRadius: theme.borderRadiusExtraLarge,
    background: theme.palette.background.paper,
    color: theme.palette.brand.text.primary,
    cursor: 'pointer',
    transition: theme.transitions.create(['border-color', 'background']),
    '&:hover': {
      borderColor: theme.palette.brand.main,
    },
  },
  selected: {
    padding: 23,
    border: `2px solid ${theme.palette.brand.main}`,
    background: theme.palette.brand.selected,
  },
  disabled: {
    opacity: 0.6,
    cursor: 'default',
    '&:hover': {
      borderColor: theme.palette.brand.inputBorder,
    },
  },
  // a check box in the shape of a circle: empty with a border, ticked in the brand colour
  check: {
    position: 'absolute',
    top: 12,
    right: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 24,
    height: 24,
    padding: 0,
    border: `1px solid ${theme.palette.brand.inputBorder}`,
    borderRadius: theme.borderRadiusPill,
    background: theme.palette.background.paper,
    '&:hover': {
      borderColor: theme.palette.brand.main,
      background: theme.palette.background.paper,
    },
    '& svg': {
      width: 14,
      height: 14,
      color: theme.palette.primary.contrastText,
    },
  },
  checkSelected: {
    borderColor: theme.palette.brand.main,
    background: theme.palette.brand.main,
    '&:hover': {
      background: theme.palette.brand.dark,
    },
  },
  // cannot be switched on (e.g. bought already): stays a white circle - doubled class, ButtonSquare greys
  // a disabled button and its hover would still react
  checkDisabled: {
    '&&, &&:hover, &&:disabled': {
      borderColor: theme.palette.brand.inputBorder,
      background: theme.palette.background.paper,
      cursor: 'default',
    },
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    paddingRight: 24,
    ...brandHeading(theme, 'h6', 'semibold'),
  },
  icon: {
    flexShrink: 0,
    width: 44,
    height: 44,
  },
  price: {
    display: 'flex',
    alignItems: 'baseline',
    flexWrap: 'wrap',
    gap: 6,
    marginTop: 16,
  },
  priceValue: {
    color: theme.palette.brand.main,
    ...brandHeading(theme, 'h4'),
  },
  priceUnit: {
    color: theme.palette.brand.text.secondary,
    fontSize: theme.brandTypography.text.small.fontSize,
  },
  actionButton: {
    ...brandOutlineButton(theme),
    fontSize: theme.brandTypography.text.regular.fontSize,
    gap: 8,
    // "Bereits gekauft": white with a grey border and no hover, as it cannot be clicked
    '&&:disabled, &&:disabled:hover': {
      borderColor: theme.palette.brand.inputBorder,
      background: theme.palette.background.paper,
      color: theme.palette.brand.text.secondary,
      cursor: 'default',
    },
  },
  stepper: {
    width: '100%',
    padding: '2px 5px',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.borderRadiusPill,
    background: theme.palette.background.paper,
    '& input': {
      flex: 1,
      height: 40,
      border: 'none',
      background: 'none',
      fontSize: theme.brandTypography.text.regular.fontSize,
      fontWeight: theme.brandTypography.weights.medium,
    },
    '& button': {
      width: 35,
      height: 35,
      borderRadius: theme.borderRadiusPill,
      background: theme.palette.background.paper,
    },
  },
  // not in the cart yet: the stepper neither reacts to hover nor counts - a click on it adds the add-on via the tile
  stepperInactive: {
    pointerEvents: 'none',
  },
  isSelected: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    color: theme.palette.brand.main,
  },
}))

interface AddonTileProps {
  type: string
  module: Module
  // current amount in the cart, 0 when not selected
  count: number
  period: string
  disabled?: boolean
  disabledHint?: string
  onChange: (type: string, module: Module, quantity: number) => void
  onRemove: (type: string, moduleName: string) => void
}

const getUnitLabel = (moduleName: string) => {
  const texts = lang.upgrade.wizard.addons
  if (moduleName === ModuleTypes.MULTI_USER) return texts.perMonthPerUser
  if (moduleName === ModuleTypes.STORAGE_PACKAGE) return texts.perMonthPerStorage(STORAGE_PACKAGE_SIZE_GB)
  return texts.perMonth
}

const AddonTile = (props: AddonTileProps) => {
  const { type, module, count, period, disabled = false, disabledHint, onChange, onRemove } = props
  const classes = useStyles()
  const texts = lang.upgrade.wizard.addons

  // same booking rules as the shop's ModuleItem
  const ownedInOtherSubs = module.singleton ? (module.otherSubscriptions || []).reduce((result, m) => result + m.owned, 0) : 0
  const max = module.bookable === 0 ? undefined : module.bookable < 0 ? 0 : module.bookable - ownedInOtherSubs
  const ownedMax = Boolean(max <= 0 && (module.owned || ownedInOtherSubs))
  const isStoragePackage = module.name === ModuleTypes.STORAGE_PACKAGE
  const isCountable = !(max <= 1 && !(isStoragePackage && (ownedMax || max >= 1)))
  const isSelected = count > 0
  const isDisabled = disabled || ownedMax
  // the amount of a countable add-on: kept when it is switched off, so switching it on again books the same amount
  const [quantity, setQuantity] = useState(count || 1)
  useEffect(() => {
    if (count > 0) setQuantity(count)
  }, [count])

  const pricePerMonth = useMemo(() => {
    const priceValues = module.prices.find(price => price.period === period)?.values?.[0]
    if (!priceValues) return 0
    return period === dateConstants.YEAR
      ? new Decimal(priceValues.pricePerDuration).dividedBy(12).toDP(2).toNumber()
      : new Decimal(priceValues.pricePerDuration).toDP(2).toNumber()
  }, [module, period])

  const title = getAddonTitle(module.name, module.name === ModuleTypes.MULTI_USER ? texts.additionalUsers(module.title) : module.title)
  const icon = getAddonIcon(module.name)
  const dataQsId = `checkout-addon-${module.name}`

  // the check circle and a click on the tile switch the add-on on and off - a countable one with the amount chosen last
  const toggle = () => {
    if (isDisabled) return
    if (isSelected) {
      onRemove(type, module.name)
      return
    }
    onChange(type, module, isCountable ? quantity : 1)
  }

  // only a selected tile reports amounts (an unselected one adds the add-on on a click, see below) -
  // "-" at 1 switches the add-on off and keeps the 1
  const handleQuantityChange = (value: number) => {
    if (!isSelected || value === count) return
    if (value < 1) {
      onRemove(type, module.name)
      return
    }
    setQuantity(value)
    onChange(type, module, value)
  }

  const action = isCountable
    ? (
      <QuantityInput
        className={clsx(classes.stepper, !isSelected && classes.stepperInactive)}
        // selected: 0 keeps "-" clickable at 1 - not selected: the amount shown disables "-"
        min={isSelected ? 0 : quantity}
        max={ownedMax ? module.owned : Math.min(max ?? getMaxCartCount(module.name), getMaxCartCount(module.name))}
        value={quantity}
        disabled={isDisabled}
        onChange={handleQuantityChange}
      />
    )
    : (
      <ButtonSquare
        className={classes.actionButton}
        variant={BUTTON_VARIANTS.outline}
        disabled={isDisabled}
        dataQsId={`${dataQsId}-toggle`}
        onClick={toggle}>
        {ownedMax && texts.owned}
        {!ownedMax && isSelected && <div className={classes.isSelected}><CheckRoundedIcon />{texts.remove}</div>}
        {!ownedMax && !isSelected && texts.add}
      </ButtonSquare>
    )

  const tile = (
    <div className={clsx(classes.root, isSelected && classes.selected, isDisabled && classes.disabled)} onClick={toggle} data-qs-id={dataQsId}>
      {/* the keyboard and screen reader way to the same toggle as the click on the tile */}
      <ButtonSquare
        className={clsx(classes.check, isSelected && classes.checkSelected, isDisabled && !isSelected && classes.checkDisabled)}
        role='checkbox'
        aria-checked={isSelected}
        aria-label={texts.toggle(title)}
        disabled={isDisabled}
        dataQsId={`${dataQsId}-check`}
        onClick={(event) => {
          event.stopPropagation()
          toggle()
        }}>
        {isSelected && <CheckRoundedIcon />}
      </ButtonSquare>
      <div>
        <div className={classes.header}>
          {icon
            ? <img className={classes.icon} src={icon} alt='' />
            : ModuleIconMap[module.name] && <SVGIcon svg={ModuleIconMap[module.name]} width='44px' height='44px' />}
          {title}
        </div>
        <div className={classes.price}>
          <span className={classes.priceValue} data-typography='heading'>{formatPrice(pricePerMonth)}</span>
          <span className={classes.priceUnit}>{getUnitLabel(module.name)}</span>
        </div>
      </div>
      {/* the controls inside the tile keep their own action - they must not toggle the tile as well.
          Only the stepper of an unselected tile does not count: a click on it adds the add-on first */}
      <div
        onClickCapture={isCountable && !isSelected
          ? (event) => {
            event.stopPropagation()
            toggle()
          }
          : undefined}
        onClick={event => event.stopPropagation()}>
        {action}
      </div>
    </div>
  )

  if (isDisabled && disabledHint) {
    return <Tooltip title={disabledHint} arrow placement='top'>{tile}</Tooltip>
  }
  return tile
}

export default AddonTile
